import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/archive';
import { Breadcrumb } from '@/components/feature/PostCard';
import { tone } from '@/utils/chigua';

const faqs = [
  {
    q: '每日吃瓜网的瓜料来源可靠吗？',
    a: '我们的内容主要基于公开报道、路透画面与多方线索整理而来，并尽量标注信息来源与时间线。涉及时效性强、正在发酵的事件，我们会持续跟进并在有权威进展时更新。',
  },
  {
    q: '你们的栏目是怎么划分的？',
    a: '我们按吃瓜话题分为五大栏目：塌房现场（人设与争议）、恋情实锤（爱豆感情）、剧组爆料（选角与片场）、综艺幕后（节目制作）、红毯名场面（造型盘点）。想追哪类瓜，进对应栏目即可。',
  },
  {
    q: '遇到不确定的"瓜"，你们会怎么处理？',
    a: '对没有实锤、仅有单方说法或聊天截图的内容，我们坚持不轻易下结论，会以"存疑""爆料称"等措辞呈现，并提醒读者理性看待。未经证实的猜测不会被当作既成事实发布。',
  },
  {
    q: '我会不会看到造谣或侵犯隐私的内容？',
    a: '我们坚决不发布捏造事实、无中生有的内容，对疑似侵犯他人隐私的画面会做必要处理。如有内容存在事实错误，欢迎通过邮箱告知，我们会及时核实并更正。',
  },
  {
    q: '我可以投稿爆料或提供线索吗？',
    a: '当然可以。你可以将文字或线索（尽量附上时间、地点等关键信息）发送到投稿邮箱 hello@meirichigua.com，我们会核实后视情况整理发布，并对投稿者信息严格保密。',
  },
  {
    q: '内容仅供娱乐参考是什么意思？',
    a: '娱乐圈八卦属于公开娱乐话题，我们的定位是提供有趣、有信息量的吃瓜内容。但任何涉及法律、名誉的结论，都应以当事人声明、司法机关和权威媒体发布为准。',
  },
];

const values = [
  { icon: 'ri-search-line', title: '先核实，再发布', desc: '对线索做多源交叉验证，标注存疑与实锤，宁缺毋滥。' },
  { icon: 'ri-emotion-line', title: '理性吃瓜', desc: '拒绝网络暴力与造谣传谣，评论引导理性，不下定义式结论。' },
  { icon: 'ri-refresh-line', title: '持续跟进', desc: '事件有新进展随时更新，让读者看到完整的时间线而非断章取义。' },
  { icon: 'ri-shield-check-line', title: '保护隐私', desc: '涉及隐私的画面做必要处理，未成年人信息一律不碰。' },
];

const steps = [
  { no: '01', title: '线索搜集', desc: '从公开报道、路透画面与多方来源收集素材，建立时间线。' },
  { no: '02', title: '交叉核实', desc: '区分实锤、存疑与传闻，宁可留白也不急于下结论。' },
  { no: '03', title: '内容创作', desc: '用轻松但不失严谨的笔调整理成瓜报，标注来源与时效。' },
  { no: '04', title: '上线迭代', desc: '发布后持续关注反馈与进展，及时更正、持续更新。' },
];

const voices = [
  { name: '资深追星人·小七', text: '这里把塌房和实锤分得清清楚楚，追星多年第一次觉得吃瓜也能这么有调理。', role: '追星十一年' },
  { name: '综艺爱好者·阿原', text: '最爱综艺幕后栏目，把剪辑逻辑讲得明明白白，看完再看综艺都"通透了"。', role: '每周追三档综艺' },
  { name: '路人粉·Melody', text: '红毯名场面盘点又毒舌又专业，造型翻车看得我脚趾抠地又停不下来。', role: '时尚博主' },
];

function Faq() {
  const [open, setOpen] = useState<number | null>(0);
  useEffect(() => {
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    };
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.text = JSON.stringify(schema);
    document.head.appendChild(el);
    return () => {
      document.head.removeChild(el);
    };
  }, []);
  return (
    <section id="faq" className="mx-auto max-w-7xl scroll-mt-20 px-4 py-12 md:px-6 md:py-16">
      <div className="grid gap-8 lg:grid-cols-2">
        <div>
          <p className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-primary-500">
            <span className="h-px w-6 bg-primary-400" aria-hidden="true" /> FAQ
          </p>
          <h2 className="font-heading text-3xl text-foreground-950">吃瓜常见问题</h2>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
            关于瓜料来源、栏目划分、内容处理与投稿方式的常见疑问，这里一次性讲清楚。
          </p>
          <Link to="/about#contact" className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600">
            <i className="ri-mail-send-line" aria-hidden="true" /> 还有其他疑问？
          </Link>
        </div>
        <div className="flex flex-col gap-3">
          {faqs.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className="overflow-hidden rounded-2xl border border-background-200 bg-background-50">
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? null : i)}
                  className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
                  aria-expanded={isOpen}
                >
                  <span className="font-semibold text-foreground-900">{f.q}</span>
                  <i className={`ri-add-line shrink-0 text-xl text-primary-500 transition-transform ${isOpen ? 'rotate-45' : ''}`} aria-hidden="true" />
                </button>
                <div className={isOpen ? 'acc-panel open' : 'acc-panel'}>
                  <p className="px-5 pb-5 text-sm leading-relaxed text-foreground-600">{f.a}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default function AboutPage() {
  useEffect(() => {
    document.title = '关于每日吃瓜网 - 内容原则 吃瓜指南 | 每日吃瓜';
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-foreground-950">
        <img
          src="https://readdy.ai/api/search-image?query=abstract%20editorial%20collage%20of%20pop%20culture%20elements%2C%20speech%20bubbles%2C%20cameras%20and%20glowing%20gossip%20headlines%20in%20bold%20red%20and%20amber%20on%20deep%20warm%20dark%20background%2C%20modern%20news%20brand%20identity%2C%20dynamic%20magazine%20style&width=1600&height=720&seq=about-hero&orientation=landscape"
          alt="关于每日吃瓜网"
          className="absolute inset-0 h-full w-full object-cover object-center opacity-45"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/95 via-foreground-950/70 to-foreground-950/40" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-4 py-14 md:px-6 md:py-20">
          <Breadcrumb items={[{ name: '首页', to: '/' }, { name: '关于我们' }]} />
          <h1 className="font-heading text-3xl text-background-50 md:text-5xl">关于每日吃瓜网</h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">
            每日吃瓜网（ChiGua Daily）是一个专注娱乐圈八卦速递的中文资讯站点。我们相信"吃瓜"不只是一种消遣，更是一面观察公共话题、内容生态与大众情绪的镜子。
          </p>
        </div>
      </section>

      {/* 定位与理念 */}
      <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="grid gap-8 lg:grid-cols-2">
          <div>
            <p className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-primary-500">
              <span className="h-px w-6 bg-primary-400" aria-hidden="true" /> Who We Are
            </p>
            <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">我们是谁，为谁而写</h2>
            <p className="mt-4 text-[15px] leading-[1.9] text-foreground-700">
              这里没有居高临下的评判，也没有低俗的猎奇。我们把明星塌房、爱豆恋情、剧组与综艺幕后、红毯造型这些话题，整理成有信息量、可读性强且相对克制的瓜报。
            </p>
            <p className="mt-3 text-[15px] leading-[1.9] text-foreground-700">
              我们的读者，是那些既想跟上热门话题、又希望在信息爆炸里少被带节奏的"理性吃瓜群众"。我们提供的是经过核实的线索、清晰的栏目和持续更新的时间线，让吃瓜这件事更有章法。
            </p>
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
              {[
                { n: '5+', l: '吃瓜栏目' },
                { n: '12+', l: '精选瓜报' },
                { n: '24h', l: '滚动快讯' },
                { n: '100%', l: '真实原创' },
              ].map((s) => (
                <div key={s.l} className="rounded-2xl bg-background-100 p-4 text-center">
                  <p className="font-heading text-2xl text-primary-600">{s.n}</p>
                  <p className="mt-1 text-xs text-foreground-600">{s.l}</p>
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-accent-600">
              <span className="h-px w-6 bg-accent-400" aria-hidden="true" /> Principles
            </p>
            <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">我们坚持的内容原则</h2>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              {values.map((v) => (
                <div key={v.title} className="rounded-2xl bg-background-100 p-5">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-accent-500 text-background-50">
                    <i className={v.icon} aria-hidden="true" />
                  </span>
                  <h3 className="mt-3 font-bold text-foreground-950">{v.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 发展理念流程 */}
      <section className="bg-background-100/70">
        <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-secondary-600">
                <span className="h-px w-6 bg-secondary-400" aria-hidden="true" /> How We Work
              </p>
              <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">一篇瓜报是这样诞生的</h2>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map((s) => (
              <div key={s.no} className="rounded-2xl bg-background-50 p-5">
                <span className="font-heading text-3xl text-secondary-300">{s.no}</span>
                <h3 className="mt-2 font-bold text-foreground-950">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 口碑 */}
      <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="mb-7">
          <p className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-primary-500">
            <span className="h-px w-6 bg-primary-400" aria-hidden="true" /> Voices
          </p>
          <h2 className="font-heading text-2xl text-foreground-950 md:text-3xl">吃瓜群众怎么说</h2>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {voices.map((v) => (
            <figure key={v.name} className="flex flex-col justify-between rounded-2xl bg-background-100 p-6">
              <div>
                <span className="font-heading text-3xl leading-none text-accent-400">“</span>
                <blockquote className="mt-1 text-sm leading-relaxed text-foreground-700">{v.text}</blockquote>
              </div>
              <figcaption className="mt-4 border-t border-background-200 pt-4">
                <p className="font-bold text-foreground-950">{v.name}</p>
                <p className="text-xs text-foreground-500">{v.role}</p>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* FAQ 全量放在关于页 */}
      <div className="border-y border-background-200/70 bg-background-100/60">
        <Faq />
      </div>

      {/* 联系 */}
      <section id="contact" className="mx-auto max-w-7xl scroll-mt-20 px-4 py-12 md:px-6 md:py-16">
        <div className="relative overflow-hidden rounded-3xl bg-foreground-900 p-6 md:p-12">
          <div className="pointer-events-none absolute -right-8 -top-8 h-48 w-48 rounded-full bg-accent-500/20 blur-2xl" aria-hidden="true" />
          <div className="pointer-events-none absolute -bottom-10 left-10 h-52 w-52 rounded-full bg-primary-500/20 blur-3xl" aria-hidden="true" />
          <div className="relative grid gap-8 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-background-50/10 px-3 py-1 text-xs font-bold text-background-50">
                <i className="ri-chat-3-fill" aria-hidden="true" /> 联系我们
              </span>
              <h2 className="mt-4 font-heading text-2xl text-background-50 md:text-3xl">投稿、纠错还是商务合作？</h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-300 md:text-base">
                无论是提供线索、指出内容错误，还是品牌合作，我们都欢迎你来信。我们会在 1–3 个工作日内回复。
              </p>
            </div>
            <div className="flex flex-col justify-center gap-4">
              <a
                href="mailto:hello@meirichigua.com"
                className="group flex items-center gap-4 rounded-2xl bg-background-50 p-4 transition-colors hover:bg-accent-100"
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-primary-500 text-background-50">
                  <i className="ri-mail-send-line text-xl" aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-xs text-foreground-500">投稿 / 纠错 / 商务</span>
                  <span className="block text-sm font-bold text-foreground-900 group-hover:text-primary-700">hello@meirichigua.com</span>
                </span>
              </a>
              <div className="flex flex-wrap gap-2">
                {categories.map((c) => (
                  <Link key={c.slug} to={`/column/${c.slug}`} className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium text-background-50 transition-colors ${tone[c.role].solid}`}>
                    <i className={c.icon} aria-hidden="true" /> {c.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}